import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
    Box,
    Typography,
    Button,
    Modal,
    CircularProgress,
    useTheme,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Avatar,
    TextField,
    InputAdornment,
    IconButton,
} from "@mui/material";

// MUI X Pro
import { DataGridPro, GridToolbar, GridActionsCellItem } from "@mui/x-data-grid-pro";
/** If you set your license in-app, uncomment:
// import { LicenseInfo } from "@mui/x-license-pro";
// LicenseInfo.setLicenseKey(process.env.REACT_APP_MUIX_PRO_KEY);
*/

import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";

import Header from "../../components/Header";
import { tokens } from "../../theme";
import ProductEntry from "../../component-files/Forms/ProductEntry";

const VendorProductsPage = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [vendorsList, setVendorsList] = useState([]);
    const [productData, setProductData] = useState([]);
    const [unitOptions, setUnitOptions] = useState([]);
    const [inventoryCategories, setInventoryCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [isProductEntryOpen, setIsProductEntryOpen] = useState(false);
    const [rowModesModel, setRowModesModel] = useState({});

    // Filters
    const [vendorFilter, setVendorFilter] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [skuFilter, setSkuFilter] = useState("");
    const [nameFilter, setNameFilter] = useState("");

    useEffect(() => {
        fetchVendors();
        fetchUnitOptions();
        fetchInventoryCategories();
        fetchVendorProducts();
    }, []);

    const fetchVendors = async () => {
        try {
            const res = await axios.get("https://figazuresql.swancreate.com/api/swa/FINANCE_Vendors");
            setVendorsList(res.data || []);
        } catch (err) {
            console.error("Failed loading vendors:", err);
        }
    };

    const fetchUnitOptions = async () => {
        try {
            const res = await axios.get("https://figazuresql.swancreate.com/api/swa/FINANCE_unitofmeasure");
            setUnitOptions((res.data || []).map((u) => ({ id: u.UnitOfMeasureID, name: u.UnitName })));
        } catch (err) {
            console.error("Failed loading UOMs:", err);
        }
    };

    const fetchInventoryCategories = async () => {
        try {
            const res = await axios.get("https://figazuresql.swancreate.com/api/swa/FINANCE_InventoryCategories");
            setInventoryCategories(
                (res.data || []).map((c) => ({
                    InventoryCategoryID: c.InventoryCategoryID,
                    InventoryCategoryName: c.InventoryCatgegoryName,
                }))
            );
        } catch (err) {
            console.error("Failed loading inventory categories:", err);
        }
    };

    const fetchVendorProducts = async () => {
        setLoading(true);
        try {
            const response = await axios.get("https://figazuresql.swancreate.com/api/swa/FINANCE_vendorproduct");
            const formatted = (response.data || []).map((p) => ({
                id: p.apiid,
                apiid: p.apiid,
                ProductImageUrl: p.ProductImageUrl || "",
                VendorProductNumber: p.VendorProductNumber || "",
                UPCBarcode: p.UPCBarcode || "",
                GTIN: p.GTIN || "",
                ProductName: p.ProductName || "",
                VendorName: p.VendorName || "",
                CategoryName: p.CategoryName || "",
                SubcategoryName: p.SubcategoryName || "",
                CasePrice: p.CasePrice ?? 0,
                UnitPrice: p.UnitPrice ?? 0,
                UnitOfMeasureID: p.UnitOfMeasureID,
                UnitofMeasureDisplay: p.UnitofMeasureDisplay,
                LowUnitNumber: p.LowUnitNumber,
                PackNum: p.PackNum,
                PackType: p.PackType,
                ContractNumber: p.ContractNumber || "",
                ContractPrice: p.ContractPrice ?? 0,
                StorageLocation: p.StorageLocation || "",
                Description: p.Description || "",
                isActive: p.isActive,
                InventoryCategoryID: p.InventoryCategoryID,
                InventoryCategoryName: p.InventoryCategoryName,
            }));
            setProductData(formatted);
            setError(null);
        } catch (err) {
            console.error(err);
            setError("Failed to load products.");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = async (id) => {
        try {
            await axios.delete(`https://figazuresql.swancreate.com/api/swa/FINANCE_vendorproduct/${id}`);
            setProductData((prev) => prev.filter((r) => r.id !== id));
        } catch {
            alert("Delete failed");
        }
    };

    const processRowUpdate = async (newRow, oldRow) => {
        const changed = {};
        Object.keys(newRow).forEach((k) => {
            if (newRow[k] !== oldRow[k]) changed[k] = newRow[k];
        });
        if (!Object.keys(changed).length) return newRow;

        await axios.put(
            `https://figazuresql.swancreate.com/api/swa/FINANCE_vendorproduct/${newRow.id}`,
            changed
        );
        setProductData((prev) => prev.map((r) => (r.id === newRow.id ? newRow : r)));
        return newRow;
    };

    const handleCellEditCommit = async ({ id, field, value }) => {
        const oldRow = productData.find((r) => r.id === id);
        if (!oldRow) return;

        let newRow = { ...oldRow, [field]: value };
        if (field === "InventoryCategoryID") {
            const sel = inventoryCategories.find((c) => c.InventoryCategoryID === value);
            newRow.InventoryCategoryName = sel ? sel.InventoryCategoryName : "";
        }
        if (field === "UnitOfMeasureID") {
            const sel = unitOptions.find((u) => u.id === value);
            newRow.UnitofMeasureDisplay = sel ? sel.name : "";
        }

        try {
            await processRowUpdate(newRow, oldRow);
            setRowModesModel((m) => ({ ...m, [id]: { mode: "View" } }));
        } catch {
            /* ignore */
        }
    };

    // Distinct lists for dropdowns
    const vendorList = useMemo(
        () => vendorsList.map((v) => v.VendorName).filter(Boolean).sort(),
        [vendorsList]
    );
    const categoryList = useMemo(() => {
        const s = new Set((productData || []).map((r) => r.CategoryName || "Uncategorized"));
        return Array.from(s).sort();
    }, [productData]);

    // Apply filters
    const filteredData = useMemo(() => {
        const sku = skuFilter.trim().toLowerCase();
        const name = nameFilter.trim().toLowerCase();

        return productData.filter((p) => {
            const vendorOk = !vendorFilter || p.VendorName === vendorFilter;
            const categoryOk = !categoryFilter || (p.CategoryName || "Uncategorized") === categoryFilter;
            const skuOk = !sku || (p.VendorProductNumber || "").toLowerCase().includes(sku);
            const nameOk = !name || (p.ProductName || "").toLowerCase().includes(name);
            return vendorOk && categoryOk && skuOk && nameOk;
        });
    }, [productData, vendorFilter, categoryFilter, skuFilter, nameFilter]);

    const clearFilters = () => {
        setVendorFilter("");
        setCategoryFilter("");
        setSkuFilter("");
        setNameFilter("");
    };

    // Columns
    const columns = [
        {
            field: "ProductImageUrl",
            headerName: "",
            width: 64,
            sortable: false,
            renderCell: (params) => (
                <Avatar src={params.value} variant="circular" sx={{ width: 32, height: 32 }} />
            ),
        },
        { field: "VendorProductNumber", headerName: "SKU", flex: 1, editable: true },
        { field: "ProductName", headerName: "Name", flex: 1.3, editable: true },
        { field: "VendorName", headerName: "Vendor", flex: 1 },
        { field: "CategoryName", headerName: "Category", flex: 1 },
        { field: "SubcategoryName", headerName: "Subcategory", flex: 1, editable: true },
        { field: "UPCBarcode", headerName: "UPC", flex: 1, editable: true },
        { field: "GTIN", headerName: "GTIN", flex: 1, editable: true },
        {
            field: "UnitofMeasureDisplay",
            headerName: "Unit",
            flex: 1,
            editable: true,
            renderEditCell: (params) => (
                <Select
                    value={params.value}
                    onChange={(e) =>
                        params.api.setEditCellValue({
                            id: params.id,
                            field: "UnitOfMeasureID",
                            value: e.target.value,
                        })
                    }
                    fullWidth
                >
                    {unitOptions.map((u) => (
                        <MenuItem key={u.id} value={u.id}>
                            {u.name}
                        </MenuItem>
                    ))}
                </Select>
            ),
        },
        { field: "LowUnitNumber", headerName: "Low #", flex: 0.7, editable: true },
        { field: "PackNum", headerName: "Pack #", flex: 0.7, editable: true },
        { field: "PackType", headerName: "Pack Type", flex: 1, editable: true },
        { field: "ContractNumber", headerName: "Contract #", flex: 1, editable: true },
        {
            field: "ContractPrice",
            headerName: "Contract Price",
            flex: 1,
            editable: true,
            valueFormatter: (p) =>
                new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(p.value),
        },
        {
            field: "UnitPrice",
            headerName: "Unit Price",
            flex: 1,
            editable: true,
            valueFormatter: (p) =>
                new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(p.value),
        },
        { field: "StorageLocation", headerName: "Storage Location", flex: 1, editable: true },
        { field: "Description", headerName: "Description", flex: 2, editable: true },
        {
            field: "isActive",
            headerName: "Active",
            type: "boolean",
            flex: 0.6,
            editable: true,
            renderCell: (p) => (p.value ? "Yes" : "No"),
        },
        {
            field: "InventoryCategoryID",
            headerName: "Inventory Category",
            flex: 1,
            editable: true,
            renderCell: (params) => params.row.InventoryCategoryName,
            renderEditCell: (params) => (
                <Select
                    value={params.value}
                    onChange={(e) => {
                        const newId = e.target.value;
                        const sel = inventoryCategories.find((c) => c.InventoryCategoryID === newId);
                        params.api.setEditCellValue({
                            id: params.id,
                            field: "InventoryCategoryID",
                            value: newId,
                        });
                        params.api.setEditCellValue({
                            id: params.id,
                            field: "InventoryCategoryName",
                            value: sel ? sel.InventoryCategoryName : "",
                        });
                    }}
                    fullWidth
                >
                    {inventoryCategories.map((c) => (
                        <MenuItem key={c.InventoryCategoryID} value={c.InventoryCategoryID}>
                            {c.InventoryCategoryName}
                        </MenuItem>
                    ))}
                </Select>
            ),
        },
        {
            field: "actions",
            type: "actions",
            headerName: "Actions",
            flex: 0.7,
            getActions: (params) => {
                const isInEdit = rowModesModel[params.id]?.mode === "Edit";
                return isInEdit
                    ? [
                        <GridActionsCellItem
                            icon={<SaveIcon sx={{ color: colors.greenAccent[400] }} />}
                            label="Save"
                            onClick={() => setRowModesModel((m) => ({ ...m, [params.id]: { mode: "View" } }))}
                            key="save"
                        />,
                        <GridActionsCellItem
                            icon={<CancelIcon />}
                            label="Cancel"
                            onClick={() =>
                                setRowModesModel((m) => ({
                                    ...m,
                                    [params.id]: { mode: "View", ignoreModifications: true },
                                }))
                            }
                            key="cancel"
                        />,
                    ]
                    : [
                        <GridActionsCellItem
                            icon={<EditIcon />}
                            label="Edit"
                            onClick={() => setRowModesModel((m) => ({ ...m, [params.id]: { mode: "Edit" } }))}
                            key="edit"
                        />,
                        <GridActionsCellItem
                            icon={<DeleteIcon />}
                            label="Delete"
                            onClick={() => handleDeleteClick(params.id)}
                            color="error"
                            key="delete"
                        />,
                    ];
            },
        },
    ];

    return (
        <Box
            m="20px"
            sx={{
                backgroundColor: colors.primary[500],
                color: colors.grey[100],
                minHeight: "100vh",
                p: 2,
                borderRadius: 1,
                boxShadow: `0px 0px 30px 5px ${colors.blueAccent[300]}33`,
            }}
        >
            <Header title="VENDOR PRODUCTS" subtitle="Manage Vendor Products" />

            {/* Top bar: Add + Filters */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1.5}>
                <Button
                    onClick={() => setIsProductEntryOpen(true)}
                    variant="contained"
                    sx={{
                        backgroundColor: colors.greenAccent[400],
                        color: colors.grey[900],
                        fontWeight: "bold",
                        "&:hover": { backgroundColor: colors.greenAccent[500] },
                    }}
                >
                    Add New Product
                </Button>

                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    {/* SKU filter */}
                    <TextField
                        label="Search SKU"
                        value={skuFilter}
                        onChange={(e) => setSkuFilter(e.target.value)}
                        size="small"
                        sx={{ minWidth: 180 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment: skuFilter ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setSkuFilter("")} aria-label="clear sku">
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                    />

                    {/* Product name filter */}
                    <TextField
                        label="Search Product"
                        value={nameFilter}
                        onChange={(e) => setNameFilter(e.target.value)}
                        size="small"
                        sx={{ minWidth: 220 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment: nameFilter ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setNameFilter("")} aria-label="clear name">
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                    />

                    {/* Vendor filter */}
                    <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
                        <InputLabel id="vendor-filter-label">Vendor</InputLabel>
                        <Select
                            labelId="vendor-filter-label"
                            value={vendorFilter}
                            label="Vendor"
                            onChange={(e) => setVendorFilter(e.target.value)}
                        >
                            <MenuItem value="">All Vendors</MenuItem>
                            {vendorList.map((v) => (
                                <MenuItem key={v} value={v}>
                                    {v}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Category filter */}
                    <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
                        <InputLabel id="category-filter-label">Category</InputLabel>
                        <Select
                            labelId="category-filter-label"
                            value={categoryFilter}
                            label="Category"
                            onChange={(e) => setCategoryFilter(e.target.value)}
                        >
                            <MenuItem value="">All Categories</MenuItem>
                            {categoryList.map((c) => (
                                <MenuItem key={c} value={c}>
                                    {c}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Button size="small" onClick={clearFilters}>
                        Clear
                    </Button>
                </Box>
            </Box>

            {loading ? (
                <CircularProgress />
            ) : error ? (
                <Typography color="error">{error}</Typography>
            ) : (
                <Box
                    height="75vh"
                    sx={{
                        "& .MuiDataGrid-root": {
                            border: "none",
                            backgroundColor: colors.primary[400],
                            color: colors.grey[100],
                        },
                        "& .MuiDataGrid-columnHeaders": {
                            backgroundColor: colors.primary[600],
                            color: colors.grey[100],
                        },
                        "& .MuiDataGrid-row:nth-of-type(even)": {
                            backgroundColor: colors.blueAccent[200] + "1A",
                        },
                        "& .MuiDataGrid-row:nth-of-type(odd)": {
                            backgroundColor: colors.blueAccent[200] + "33",
                        },
                    }}
                >
                    <DataGridPro
                        rows={filteredData}
                        columns={columns}
                        getRowId={(row) => row.id}
                        // Pro goodies
                        disableSelectionOnClick
                        columnReordering
                        initialState={{
                            pinnedColumns: { left: ["VendorProductNumber", "ProductName"], right: ["actions"] },
                        }}
                        // Toolbar with quick filter
                        components={{ Toolbar: GridToolbar }}
                        componentsProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 300 },
                            },
                        }}
                        // Editing
                        editMode="cell"
                        rowModesModel={rowModesModel}
                        onRowModesModelChange={(m) => setRowModesModel(m)}
                        processRowUpdate={processRowUpdate}
                        onProcessRowUpdateError={(e) => console.error(e)}
                        onCellEditCommit={handleCellEditCommit}
                        // Pagination
                        pageSize={10}
                        rowsPerPageOptions={[10, 20, 50]}
                        density="comfortable"
                    />
                </Box>
            )}

            <Modal
                open={isProductEntryOpen}
                onClose={() => setIsProductEntryOpen(false)}
                sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
            >
                <Box
                    sx={{
                        backgroundColor: colors.primary[500],
                        p: 3,
                        borderRadius: 1,
                        width: "90vw",
                        height: "80vh",
                        color: colors.grey[100],
                        boxShadow: 24,
                    }}
                >
                    <Typography variant="h6" mb={2}>
                        Add Product
                    </Typography>
                    <ProductEntry />
                </Box>
            </Modal>
        </Box>
    );
};

export default VendorProductsPage;
